<template>
  <md-form ref="loginFormRef" class="login-pwd-form" :model="formData" :rules="rules" label-position="top">
    <md-form-item prop="account">
      <md-input
        v-model.trim="formData.account"
        size="large"
        maxlength="20"
        name="account"
        placeholder="请输入账号"
        clearable
        @input="removeAccountSpaces"
        @keydown="preventSpace"
      />
    </md-form-item>
    <md-form-item prop="secrectKey" class="password-item">
      <md-input
        v-model.trim="formData.secrectKey"
        size="large"
        type="password"
        placeholder="请输入密码"
        show-password
        @input="removePwdSpaces"
        @keydown="preventSpace"
        @keyup.enter="handleLogin(loginFormRef)"
      />
    </md-form-item>
    <div class="login-btn">
      <md-checkbox v-model="isRemember" size="small" label="记住账号" />
      <!--<md-button class="password-btn" type="text" size="small" @click="handleReset">忘记密码?</md-button> -->
    </div>
    <md-button class="submit-btn" type="primary" size="large" :loading="loading" @click="handleLogin(loginFormRef)"
      >立即登录</md-button
    >
    <agreement-reg
      ref="agreementRegRef"
      :is-agreement="isAgreement"
      :loading="loading"
      :is-pwd-login="true"
      :submit-form-fn="submitForm"
      @update:isAgreement="changeAgreement"
      @update:loading="changeLoading"
    />
  </md-form>
</template>

<script setup lang="ts">
import { ref, inject, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import '@/api/jsencrypt.js';
import { MdForm, MdFormItem, MdInput, MdButton, MdCheckbox } from 'mdesign3';
import { FormRulesMap } from 'mdesign3/lib/components/form/src/form.type';
import { STORE_ACCOUNT, secrect } from '@/config/const';
import { getPublicKey, encSsoLogin, getUserRole, getControlParam } from '@/api/userApi';
import { ILoginByPwdParams, IStorageResourceParams } from '@/utils/interface';
import config from '@/config/user.env';
import AgreementReg from './agreementReg.vue';
import { storageResource, preInitSystem } from './utilsLogin';
import { prefetchUserApplications, prefetchUserRoleResources } from '@/utils/roleResourcesCache';
import { prefetchHomeMicroAppOnLoginPage } from '@/utils/prefetchMicroAppsByAuthority';
import { useStore } from 'vuex';
import { UserRoleHelper } from '@/utils/userRoleHelper';

const cacheAccount = localStorage.getItem(STORE_ACCOUNT);
const loading = ref<Boolean>(false);
const isAgreement = ref<Boolean>(false);
const isRemember = ref<Boolean>(!!cacheAccount);
const route = useRoute();
const router = useRouter();
const store = useStore();
const agreementRegRef = ref<InstanceType<typeof AgreementReg> | null>(null);
const receiveAccountData = inject<(accountData: any) => void>('receiveAccountData');
type FormInstance = InstanceType<typeof MdForm>;
const loginFormRef = ref<FormInstance>();

const formData = ref<ILoginByPwdParams>({
  account: cacheAccount || '',
  secrectKey: '',
});

/**
 * 登录页预加载即将打开的首页子应用（有停留提前量，进入后挂载可走 disk cache）。
 * - 输入账号：防抖 300ms；点登录 flush（同 app 去重）
 * - redirectUrl 只影响「预哪个」，不单独开预取通道
 * - 强刷新首页不走此逻辑（与挂载间隔过短，预加载无作用）
 */
let accountPrefetchTimer: ReturnType<typeof setTimeout> | null = null;
/** 取消未完成的防抖并立刻按当前账号预首页 */
const flushHomePrefetchByAccount = (account?: string) => {
  if (accountPrefetchTimer) {
    clearTimeout(accountPrefetchTimer);
    accountPrefetchTimer = null;
  }
  prefetchHomeMicroAppOnLoginPage((account ?? formData.value.account ?? '').trim());
};
const scheduleHomePrefetchByAccount = (account: string) => {
  if (accountPrefetchTimer) {
    clearTimeout(accountPrefetchTimer);
  }
  accountPrefetchTimer = setTimeout(() => {
    prefetchHomeMicroAppOnLoginPage(account.trim());
  }, 300);
};

// 记住账号，或仅带 redirect 进入登录页：立刻按解析结果预首页
onMounted(() => {
  if (formData.value.account || route.query.redirectUrl) {
    flushHomePrefetchByAccount(formData.value.account);
  }
});

watch(
  () => formData.value.account,
  (account) => {
    if (account) {
      scheduleHomePrefetchByAccount(account);
    }
  },
);

const getEncKey = (publicKey: string, pwd: string) => {
  const encrypt = new JSEncrypt();
  encrypt.setPublicKey(publicKey);
  const password = encrypt.encrypt(pwd);
  return password;
};

const rules: FormRulesMap = {
  account: [
    {
      required: true,
      message: '请输入账号',
    },
  ],
  password: [
    {
      required: true,
      message: '请输入密码',
    },
  ],
};

const changeAgreement = (val: boolean) => {
  isAgreement.value = val;
};

const changeLoading = (val: boolean) => {
  loading.value = val;
};

const removeAccountSpaces = (value) => {
  // 移除所有空格并更新模型
  formData.value.account = value.replace(/\s+/g, '');
};

const removePwdSpaces = (value) => {
  // 移除所有空格并更新模型
  formData.value.secrectKey = value.replace(/\s+/g, '');
};
const preventSpace = (event) => {
  if (event.key === ' ') {
    event.preventDefault(); // 阻止空格键的默认行为
  }
};

const submitForm = async () => {
  const account = formData.value.account;
  // 点登录时同步预首页：输入后立刻点登录时防抖可能未触发；同 app 去重不会重复拉取
  flushHomePrefetchByAccount(account);
  const secrectData = await getPublicKey();
  const publicKey = secrectData?.data || secrect;
  const secrectKey = getEncKey(publicKey, formData.value.secrectKey);

  const data: ILoginByPwdParams = {
    account,
    secrectKey,
  };
  encSsoLogin(data)
    .then(async (res) => {
      loading.value = false;
      if (res.code === '0') {
        if (isRemember.value) {
          localStorage.setItem(STORE_ACCOUNT, account);
        } else {
          localStorage.removeItem(STORE_ACCOUNT);
        }
        const params = {
          userCode: res.data.userCode,
          positionCode: 'main',
          applicationCode: config.applicationCode,
        };
        const userInfo = res.data;
        store.commit('updateAccessToken', userInfo.accessToken);
        localStorage.setItem('accessToken', userInfo.accessToken);
        const currentTenant = res.data.currentTenant;
        const roles = await getUserRole(params);
        if (!roles?.data || roles?.data.length === 0) {
          UserRoleHelper.showNoRoleMessage();
          return;
        }
        if (roles.code === '0' && roles?.data.length > 0) {
          const roleList = roles?.data.map((item) => {
            const roleObj = { ...item, userName: res.data.userName };
            return roleObj;
          });
          getControlParamFn(roleList, userInfo, currentTenant, res.data.userName);
        }
      }
    })
    .catch((err) => {
      loading.value = false;
    });
};

// 获取角色组
const getControlParamFn = async (accountDatas: any, userInfo: any, currentTenant: any, userName: string) => {
  let roleList = [];
  let roleGroupData = [];
  try {
    const params = {
      applicationCode: config.applicationCode,
      code: 'EL_ROLE_GROUP',
    };
    const res = await getControlParam(params);
    const resData = res.data?.value;
    const roleGroupResult = JSON.parse(resData);
    // console.log('===roleGroupResult:',roleGroupResult);
    const roleGroupList =
      roleGroupResult &&
      roleGroupResult.map((item: any) =>
        item.roleGroupValue.map((e: any) => {
          e.roleGroupCode = item.roleGroupCode;
          e.roleGroupName = item.roleGroupName;
          return e;
        }),
      );
    roleList = roleGroupList && roleGroupList.flat();
    // console.log('===roleList:',roleList);
  } catch (error) {
    roleList = [];
  }
  const roleCodeArr = roleList && roleList.map((item: any) => item.roleCode);

  if (roleCodeArr && roleCodeArr.length > 0) {
    let tableData = [];
    const tableList = accountDatas;
    let innerGroup = tableList.filter((item: any) => roleCodeArr.includes(item.roleCode));
    const outerGroup = tableList.filter((item: any) => !roleCodeArr.includes(item.roleCode));
    if (innerGroup && innerGroup.length > 0) {
      innerGroup = innerGroup.map((item: any) => {
        item.roleName = roleList.find((role: any) => role.roleCode === item.roleCode)?.roleGroupName || item.roleName;
        item.roleGroupCode = roleList.find((role: any) => role.roleCode === item.roleCode)?.roleGroupCode;
        return item;
      });
      const groupedDealData = innerGroup.reduce((acc: any, current: any) => {
        const groupCode = current.roleGroupCode;
        if (!acc[groupCode]) {
          acc[groupCode] = [];
        }
        acc[groupCode].push(current);
        return acc;
      }, {});
      // console.log("groupedDealData:",groupedDealData);
      const groupedDealDataArr = Object.keys(groupedDealData).map((key) => groupedDealData[key]);
      // console.log("groupedDealDataArr:",groupedDealDataArr);
      let uniquesGroup: any[] = [];
      groupedDealDataArr.map((group) => {
        const tableListArr = group.map((item: any) => item.userCode);
        let uniques = tableListArr.filter((value: any, index: any, self: any) => self.indexOf(value) === index);
        if (uniques && uniques.length > 0) {
          uniques = uniques.map((item: any) => {
            const roleArr = group.filter((role: any) => role.userCode === item);
            const roleCodes = roleArr.map((role: any) => role.roleCode);
            const roleObj = roleArr && roleArr[0];
            roleObj.roleCode = roleCodes.join(',');
            // roleObj.userName = userName;
            return roleObj;
          });
          uniquesGroup.push(...uniques);
          // console.log('===map uniques:',uniques);
        }
      });
      // console.log('===uniquesGroup:',uniquesGroup);
      tableData = [...uniquesGroup, ...outerGroup];
    } else {
      tableData = [...tableList];
    }
    // roleGroupData = tableData.map((item) => {
    //   const roleObj = { ...item, userName };
    //   return roleObj;
    // });
    roleGroupData = tableData;
  } else {
    roleGroupData = accountDatas;
  }
  // console.log('===roleGroupData:',roleGroupData);
  // 过滤掉v3试点角色处理
  if (import.meta.env.MODE === 'production') {
    roleGroupData = roleGroupData.filter((role: any) => role.roleCode !== 'RL202512250538')
    if (roleGroupData.length === 0) {
      MdMessage.error('您当前没有系统角色，请联系系统管理员');
      return;
    }
  }

  const params: IStorageResourceParams = {
    roleCode: roleGroupData && roleGroupData[0].roleCode,
    roleList: roleGroupData,
    userInfo,
    currentTenant,
  };

  if (roleGroupData && roleGroupData.length === 1) {
    if (loading.value) return;
    const roleCode = roleGroupData[0].roleCode;
    prefetchUserApplications(userInfo.userCode);
    prefetchUserRoleResources(userInfo.userCode, roleCode);
    loading.value = true;
    await storageResource(params);
    const initialized = await preInitSystem();
    loading.value = false;
    if (!initialized) return;
    if (route.query.redirectUrl) {
      router.replace(`${decodeURIComponent(route.query.redirectUrl as string)}`);
    } else {
      router.replace('/');
    }
  } else if (roleGroupData && roleGroupData.length > 1) {
    prefetchUserApplications(userInfo.userCode);
    receiveAccountData &&
      receiveAccountData({
        storageResourceParams: params,
        mode: 'PWD',
      });
  }
};

const handleLogin = (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  formEl.validate((valid) => {
    if (valid) {
      loading.value = true;
      if (!isAgreement.value) {
        agreementRegRef.value?.agreementMsg();
        return;
      }

      submitForm();
    } else {
      return false;
    }
  });
};

// const handleReset = () => {};
</script>

<style lang="less" scoped>
.login-pwd-form {
  .login-btn {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}
</style>
